import { useEffect, useRef, useState } from 'react';
import { ALL_SPECIALISATIONS } from '@kennisbank/shared';
import { ChevronDownIcon, SearchIcon, XIcon } from './icons.js';

/**
 * OneView Documents filter rail (Phase 3).
 *
 * A controlled left-rail filter surface for /documents. Owns no state of
 * its own beyond the popovers — every value/setter is hoisted to the page
 * so the existing server-driven query pipeline (q / author / type /
 * specialisation) keeps working unchanged, and the new client-side
 * filters (ring / classification / source type) layer on top.
 *
 * The rail itself is sticky-top inside its scroll container so the page
 * table scrolls independently while the filter controls stay visible.
 */

export type RingKey = 'ring0' | 'ring1' | 'ring2';
export type ClassificationKey = 'public' | 'internal' | 'confidential' | 'restricted';
const RING_OPTIONS: { key: RingKey; label: string; dotClass: string }[] = [
  { key: 'ring0', label: 'Ring 0 — Internal', dotClass: 'bg-nebula-gray-3' },
  { key: 'ring1', label: 'Ring 1 — Product', dotClass: 'bg-nebula-secondary' },
  { key: 'ring2', label: 'Ring 2 — Verified external', dotClass: 'bg-nebula-info' },
];

const CLASSIFICATION_OPTIONS: { key: ClassificationKey; label: string }[] = [
  { key: 'public', label: 'Public' },
  { key: 'internal', label: 'Internal' },
  { key: 'confidential', label: 'Confidential' },
  { key: 'restricted', label: 'Restricted' },
];

interface Props {
  q: string;
  onQChange: (v: string) => void;

  authors: { name: string; count: number }[];
  authorFilter: string;
  onAuthorChange: (v: string) => void;

  documentTypes: string[];
  typeFilter: string;
  onTypeChange: (v: string) => void;

  ringFilter: Set<RingKey>;
  onToggleRing: (r: RingKey) => void;

  classificationFilter: Set<ClassificationKey>;
  onToggleClassification: (c: ClassificationKey) => void;

  selectedSpecs: Set<string>;
  onToggleSpec: (s: string) => void;

  /** Live admin-managed vocabulary, ring-tagged. When omitted, falls
   *  back to the hardcoded ALL_SPECIALISATIONS (without ring labels). */
  availableSpecialisationsByRing?: { name: string; ring: 'ring0' | 'ring1' }[];

  hasActiveFilters: boolean;
  onClear: () => void;
}

export function DocumentsFilterRail(props: Props): JSX.Element {
  return (
    <aside className="thin-scroll sticky top-0 h-full w-[260px] flex-shrink-0 overflow-y-auto border-r border-nebula-border-soft bg-nebula-surface">
      <div className="space-y-5 px-4 py-5">
        <div className="flex items-center justify-between">
          <h2 className="ov-h-card">Filters</h2>
          {props.hasActiveFilters && (
            <button
              type="button"
              onClick={props.onClear}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-nebula-secondary hover:bg-nebula-tint"
            >
              <XIcon size={12} /> Clear
            </button>
          )}
        </div>

        <FilterGroup label="Search">
          <div className="relative">
            <SearchIcon
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-nebula-fg-4"
            />
            <input
              type="search"
              value={props.q}
              onChange={(e) => props.onQChange(e.target.value)}
              placeholder="Search documents..."
              className="ov-body w-full rounded-md border border-nebula-border bg-nebula-surface py-1.5 pl-8 pr-2 placeholder:text-nebula-fg-4 focus:border-nebula-secondary focus:outline-none"
            />
          </div>
        </FilterGroup>

        <FilterGroup label="Ring">
          <div className="space-y-1.5">
            {RING_OPTIONS.map((opt) => (
              <CheckboxRow
                key={opt.key}
                checked={props.ringFilter.has(opt.key)}
                onChange={() => props.onToggleRing(opt.key)}
                label={
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${opt.dotClass}`} aria-hidden />
                    {opt.label}
                  </span>
                }
              />
            ))}
          </div>
        </FilterGroup>

        <FilterGroup label="Classification">
          <div className="space-y-1.5">
            {CLASSIFICATION_OPTIONS.map((opt) => (
              <CheckboxRow
                key={opt.key}
                checked={props.classificationFilter.has(opt.key)}
                onChange={() => props.onToggleClassification(opt.key)}
                label={opt.label}
              />
            ))}
          </div>
        </FilterGroup>

        {props.authors.length > 0 && (
          <FilterGroup label="Author">
            <select
              value={props.authorFilter}
              onChange={(e) => props.onAuthorChange(e.target.value)}
              title={props.authorFilter || 'All authors'}
              className="ov-body w-full truncate rounded-md border border-nebula-border bg-nebula-surface px-2 py-1.5 focus:border-nebula-secondary focus:outline-none"
            >
              <option value="">All authors</option>
              {props.authors.map((a) => (
                <option key={a.name} value={a.name}>
                  {a.name} ({a.count})
                </option>
              ))}
            </select>
          </FilterGroup>
        )}

        {props.documentTypes.length > 0 && (
          <FilterGroup label="Document type">
            <select
              value={props.typeFilter}
              onChange={(e) => props.onTypeChange(e.target.value)}
              className="ov-body w-full rounded-md border border-nebula-border bg-nebula-surface px-2 py-1.5 focus:border-nebula-secondary focus:outline-none"
            >
              <option value="">All types</option>
              {props.documentTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FilterGroup>
        )}

        <FilterGroup label="Specialisations">
          <SpecialisationsMultiSelect
            selected={props.selectedSpecs}
            onToggle={props.onToggleSpec}
            availableByRing={props.availableSpecialisationsByRing}
          />
        </FilterGroup>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Internal primitives

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="ov-meta-upper">{label}</div>
      {children}
    </div>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: React.ReactNode;
}): JSX.Element {
  return (
    <label className="ov-body flex cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 hover:bg-nebula-tint">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 cursor-pointer rounded border-nebula-border accent-nebula-primary"
      />
      <span className="text-nebula-fg-2">{label}</span>
    </label>
  );
}

/**
 * Compact multi-select for the specialisation vocabulary. Selected
 * options render inline as removable chips; a dashed "+ Add" button
 * opens a popover with the remaining canonical entries. Reuses the
 * vocabulary from `@kennisbank/shared` (same source as
 * InlineSpecialisationsCell so the two stay in sync).
 */
function SpecialisationsMultiSelect({
  selected,
  onToggle,
  availableByRing,
}: {
  selected: Set<string>;
  onToggle: (s: string) => void;
  availableByRing?: { name: string; ring: 'ring0' | 'ring1' }[];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Build vocabulary as ring-tagged items. When live data hasn't loaded
  // yet, fall back to the hardcoded list (no ring label — we don't have
  // ring metadata for the static strings).
  const vocabulary: { name: string; ring?: 'ring0' | 'ring1' }[] =
    availableByRing && availableByRing.length > 0
      ? availableByRing
      : ALL_SPECIALISATIONS.map((name) => ({ name }));
  // Sort: Ring 0 (Internal) first then Ring 1 (Product), alphabetical within.
  const sorted = [...vocabulary].sort((a, b) => {
    const ringRank = (r?: string): number => (r === 'ring0' ? 0 : r === 'ring1' ? 1 : 2);
    const dr = ringRank(a.ring) - ringRank(b.ring);
    return dr !== 0 ? dr : a.name.localeCompare(b.name);
  });
  const remaining = sorted.filter((s) => !selected.has(s.name));

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap gap-1">
        {Array.from(selected).map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded-full bg-nebula-tint px-2 py-0.5 text-[11px] font-medium text-nebula-secondary"
          >
            {s}
            <button
              type="button"
              onClick={() => onToggle(s)}
              aria-label={`Remove ${s}`}
              className="rounded-full p-0.5 hover:bg-white/60"
            >
              <XIcon size={10} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={remaining.length === 0}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-nebula-border px-2 py-0.5 text-[11px] text-nebula-fg-3 hover:bg-nebula-tint disabled:opacity-40"
        >
          + Add <ChevronDownIcon size={10} />
        </button>
      </div>
      {open && remaining.length > 0 && (
        <div className="thin-scroll absolute left-0 top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-nebula-border bg-nebula-surface p-1 shadow-lg">
          {remaining.map((s, idx) => {
            // Section header when the ring changes.
            const prev = idx > 0 ? remaining[idx - 1] : undefined;
            const showHeader = !prev || prev.ring !== s.ring;
            const headerLabel =
              s.ring === 'ring0'
                ? 'Ring 0 — Internal'
                : s.ring === 'ring1'
                  ? 'Ring 1 — Product'
                  : '';
            return (
              <div key={`${s.ring ?? 'none'}-${s.name}`}>
                {showHeader && headerLabel && (
                  <div className="mt-1 px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-nebula-fg-3">
                    {headerLabel}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onToggle(s.name);
                  }}
                  className="ov-body flex w-full items-center justify-between rounded px-2 py-1 text-left text-[12px] text-nebula-fg-2 hover:bg-nebula-tint"
                >
                  <span>{s.name}</span>
                  {s.ring && (
                    <span
                      className={
                        'ml-2 inline-block h-1.5 w-1.5 rounded-full ' +
                        (s.ring === 'ring0' ? 'bg-nebula-gray-3' : 'bg-nebula-secondary')
                      }
                      aria-label={s.ring === 'ring0' ? 'Ring 0' : 'Ring 1'}
                    />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
